import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import {
  ComponentSourceType,
  ComponentType,
  CompositeActualValue,
  CompositeFunction,
  CompositeType,
  evaluateCompositeMetricValue,
  parseCompositeActualValue,
} from 'types/components'

const getTypeDisplayName = (type: ComponentType) => {
  switch (type) {
    case ComponentType.COMPONENT_TYPE_INTEGER:
      return 'Integer'
    case ComponentType.COMPONENT_TYPE_CURRENCY:
      return 'Currency'
    case ComponentType.COMPONENT_TYPE_PERCENTAGE:
      return 'Percentage'
    case ComponentType.COMPONENT_TYPE_BOOL:
      return 'Boolean'
    case ComponentType.COMPONENT_TYPE_FLOAT:
      return 'Float'
    default:
      return type
  }
}

interface ComponentDisplayTooltipProps {
  componentType: ComponentType
  value: string | number | boolean | null
  currency?: string
  currencyDecimals?: number
  decimalPlaces?: number
  numericDecimalPlaces?: number
  typographyComponent?: React.ElementType
  sourceType?: ComponentSourceType
  compositeFunction?: CompositeFunction
  compositeType?: CompositeType
  lowerIsBetter?: boolean
}

const ComponentDisplayTooltip: React.FC<ComponentDisplayTooltipProps> = ({
  componentType,
  value,
  currency = 'USD',
  currencyDecimals = 0,
  decimalPlaces = 2,
  numericDecimalPlaces,
  sourceType = ComponentSourceType.COMPONENT_SOURCE_MANUAL,
  compositeFunction = CompositeFunction.COMPOSITE_FUNCTION_NONE,
  compositeType = CompositeType.COMPOSITE_TYPE_SIMPLE,
  lowerIsBetter = false,
}) => {
  const [compositeData, setCompositeData] = useState<CompositeActualValue | null>(null)
  const theme = useTheme()

  useEffect(() => {
    if (sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE && typeof value === 'string') {
      setCompositeData(parseCompositeActualValue(value))
    }
  }, [value, sourceType])

  const getFormattedValue = (inputValue: any, overrideType?: ComponentType, overrideDecimals?: number) => {
    const typeToUse = overrideType || componentType
    // Use numericDecimalPlaces if available, otherwise fall back to overrideDecimals or decimalPlaces
    const effectiveDecimals =
      numericDecimalPlaces !== undefined
        ? numericDecimalPlaces
        : overrideDecimals !== undefined
          ? overrideDecimals
          : decimalPlaces

    switch (typeToUse) {
      case ComponentType.COMPONENT_TYPE_CURRENCY:
        const currencyValue = Number(inputValue)
        const currencyDecimalPlaces =
          numericDecimalPlaces !== undefined
            ? numericDecimalPlaces
            : overrideDecimals !== undefined
              ? overrideDecimals
              : currencyDecimals
        // Check if the value is a whole number to determine if we need decimals
        const needsDecimals = currencyValue % 1 !== 0
        const decimalsToUse = needsDecimals ? currencyDecimalPlaces : 0

        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: decimalsToUse,
          maximumFractionDigits: decimalsToUse,
        }).format(currencyValue)
      case ComponentType.COMPONENT_TYPE_INTEGER:
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Number(inputValue))
      case ComponentType.COMPONENT_TYPE_FLOAT:
        const floatValue = Number(inputValue)
        // Check if the value is a whole number to determine if we need decimals
        const isWholeNumber = floatValue % 1 === 0
        const floatDecimals = isWholeNumber ? 0 : effectiveDecimals

        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: floatDecimals,
          maximumFractionDigits: floatDecimals,
        }).format(floatValue)
      case ComponentType.COMPONENT_TYPE_PERCENTAGE:
        const percentValue = Number(inputValue)
        // Check if the percentage value (after multiplying by 100) is a whole number
        const isWholePercentage = (percentValue * 100) % 1 === 0

        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: isWholePercentage ? 0 : effectiveDecimals,
          maximumFractionDigits: isWholePercentage ? 0 : effectiveDecimals,
        }).format(percentValue)
      case ComponentType.COMPONENT_TYPE_BOOL:
        if (typeof inputValue === 'string') {
          const lowercaseValue = inputValue.toLowerCase()
          const truthyValues = ['true', '1', 't', 'y', 'yes']
          return truthyValues.includes(lowercaseValue) ? 'True' : 'False'
        }
        return inputValue ? 'True' : 'False'
      default:
        return inputValue?.toString() ?? ''
    }
  }

  const getCompositeDisplayValue = (inputValue: string) => {
    if (!inputValue) return ''

    const compositeReduction = evaluateCompositeMetricValue(inputValue, {
      compositeFunction,
      compositeType,
      lowerIsBetter,
      fallbackComponentType: componentType,
    })

    const reducedValue = compositeReduction.reducedValue
    const useDecimalPlaces =
      compositeReduction.functionUsed === CompositeFunction.COMPOSITE_FUNCTION_AVERAGE ||
      compositeReduction.functionUsed === CompositeFunction.COMPOSITE_FUNCTION_STDDEV

    if (useDecimalPlaces) {
      if (compositeType === CompositeType.COMPOSITE_TYPE_COMPLEX) {
        return `${getFormattedValue(reducedValue, ComponentType.COMPONENT_TYPE_PERCENTAGE)}*`
      }

      if (componentType === ComponentType.COMPONENT_TYPE_CURRENCY) {
        const decimalsToUse = numericDecimalPlaces !== undefined ? numericDecimalPlaces : decimalPlaces
        return `${getFormattedValue(reducedValue, undefined, decimalsToUse)}*`
      }

      if (componentType === ComponentType.COMPONENT_TYPE_INTEGER) {
        return `${getFormattedValue(reducedValue, ComponentType.COMPONENT_TYPE_FLOAT)}*`
      }

      return `${getFormattedValue(reducedValue)}*`
    }

    return `${getFormattedValue(reducedValue)}*`
  }

  const displayValue =
    sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE && typeof value === 'string'
      ? getCompositeDisplayValue(value)
      : getFormattedValue(value)

  return (
    <>
      {sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE && (
        <Box
          sx={{
            width: '900px', // Increased from 700px
            height: '300px',
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: '21px',
            border: `3px solid ${theme.palette.primary.main}`,
            p: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">Composite Function: {compositeFunction}</Typography>
            {compositeData && (
              <Typography variant="subtitle1" fontWeight="medium" sx={{ mr: 3 }}>
                Applied Value: {displayValue.replace('*', '')}
              </Typography>
            )}
          </Box>

          {compositeData && (
            <TableContainer
              sx={{
                mt: 2,
                bgcolor: theme.palette.background.paper,
                borderRadius: '8px',
                border: `2px solid ${theme.palette.divider}`,
              }}
            >
              <Table stickyHeader size="small" aria-label="composite values table">
                <TableHead
                  sx={{
                    '& th': {
                      borderTop: `1px solid ${theme.palette.divider}`,
                      borderBottom: `2px solid ${theme.palette.divider} !important`,
                    },
                    '& th:first-of-type': {
                      borderTopLeftRadius: '8px',
                    },
                    '& th:last-child': {
                      borderTopRightRadius: '8px',
                    },
                  }}
                >
                  <TableRow>
                    <TableCell
                      sx={{
                        width: 200, // Increased from 120
                        maxWidth: 200, // Increased from 120
                        position: 'sticky !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      Label
                    </TableCell>
                    <TableCell
                      sx={{
                        width: 300, // Increased from 185
                        maxWidth: 300, // Increased from 185
                        position: 'sticky !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      sx={{
                        width: 65, // Increased from 60
                        maxWidth: 65, // Increased from 60
                        position: 'sticky !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        width: 90,
                        maxWidth: 90,
                        position: 'sticky !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      Target
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        width: 90,
                        maxWidth: 90,
                        position: 'sticky !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      Value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {compositeData.values.map((item, index) => (
                    <TableRow key={index} sx={{ py: 3 }} role="row" tabIndex={-1}>
                      <TableCell sx={{ whiteSpace: 'nowrap', width: 200, maxWidth: 200, overflow: 'hidden' }}>
                        {item.label}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', width: 300, maxWidth: 300, overflow: 'hidden' }}>
                        {item.description}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', width: 100, maxWidth: 100, overflow: 'hidden' }}>
                        {getTypeDisplayName(item.componentType || componentType)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: 'nowrap', width: 90, maxWidth: 90, overflow: 'hidden' }}
                      >
                        {item.target ? getFormattedValue(item.target, item.componentType || componentType) : '-'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: 'nowrap', width: 90, maxWidth: 90, overflow: 'hidden' }}
                      >
                        {getFormattedValue(item.value, item.componentType || componentType)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </>
  )
}

export default ComponentDisplayTooltip
